import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import {
  ClipboardList, ChevronLeft, Loader2, AlertTriangle, Building2, Mic, FileSignature,
  CalendarClock, Check, Paperclip, Plus, Trash2, Link2, MessageSquareQuote,
  CheckCircle2, PauseCircle, CalendarPlus,
} from 'lucide-react'
import api from '@/lib/api'
import RdcDiretoria from '@/components/rdc/RdcDiretoria'

/**
 * Tela unica do RDC: abre a demanda, encerra a coleta e conduz o resto.
 *
 * Era um modal e depois duas telas; virou uma so porque o registro e um processo,
 * nao um cadastro. Quem encerra a coleta continua aqui vendo o prazo que disparou,
 * a agenda que precisa sair e o parecer quando chegar — sem trocar de lugar.
 *
 * `/rdc/novo` abre vazia; `/rdc/<id>` abre um registro existente. Ao criar, a rota
 * e trocada por replace: o voltar do navegador nao devolve um formulario que ja virou
 * registro.
 */

type Opp = {
  id: string; name: string; value?: number; stage?: string
  lead_title?: string; lead_company_name?: string
  lead_sub_origin?: string; owner_name?: string
}

const TIPOS = [
  { v: 'A', t: 'Escopo padrão, já contratado',
    d: 'Licenciamento em condições padronizadas, renovação sem alteração de escopo, ou funcionalidade que você sabe e pode comprovar que existe na versão corrente.',
    prazo: 2 },
  { v: 'B', t: 'Funcionalidade não confirmada',
    d: 'Você acredita que existe, mas não tem confirmação na versão corrente nem registro em roadmap.',
    prazo: 2 },
  { v: 'C', t: 'Nova funcionalidade, melhoria ou UX/UI',
    d: 'Recurso inexistente, ajuste ou melhoria dentro de um produto da Nai.',
    prazo: 5 },
  { v: 'D', t: 'Projeto aderente à linha de produto',
    d: 'Projeto novo que se apoia nos produtos e nas competências da Nai.',
    prazo: 10 },
  { v: 'E', t: 'Sem aderência à linha de produto',
    d: 'Não se relaciona com o que a Nai oferece. Não se oferta: registra-se e escalona ao Diretor Presidente.',
    prazo: null },
]

const CATEGORIAS = [
  { v: 'reuniao_cliente', t: 'Gravação ou transcrição da reunião', prova: true },
  { v: 'confirmacao_escrita', t: 'Confirmação escrita enviada ao cliente', prova: true },
  { v: 'agenda_passagem', t: 'Gravação ou transcrição da passagem', prova: false },
  { v: 'parecer', t: 'Parecer técnico', prova: false },
  { v: 'financeiro', t: 'Manifestação do Financeiro', prova: false },
  { v: 'outro', t: 'Outro documento de apoio', prova: false },
]

const FASES = [
  { v: 'prospect', t: 'Prospect' },
  { v: 'negociacao', t: 'Em negociação' },
  { v: 'cliente', t: 'Cliente contratado' },
]

const URGENCIAS = [{ v: 'alta', t: 'Alta' }, { v: 'media', t: 'Média' }, { v: 'baixa', t: 'Baixa' }]

const ESTAGIOS: Record<string, string> = {
  qualified: 'Qualificada', proposal_sent: 'Proposta enviada', negotiation: 'Negociação',
  closing: 'Fechamento', won: 'Ganha', lost: 'Perdida',
}

const ESTADOS: Record<string, { t: string; cls: string }> = {
  coleta: { t: 'Em coleta', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  registrado: { t: 'Aguardando agenda', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  passagem_agendada: { t: 'Passagem agendada', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  acordo_pendente: { t: 'De acordo pendente', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  de_acordo: { t: 'De acordo', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  parecer_preliminar: { t: 'Parecer preliminar', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  parecer_conclusivo: { t: 'Parecer conclusivo', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  financeiro_pendente: { t: 'Aguardando Financeiro', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  habilitado: { t: 'Habilitado para oferta', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  deliberacao: { t: 'Em deliberação', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  encerrado: { t: 'Encerrado', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
}

const moeda = (v?: number) =>
  typeof v === 'number' && v > 0
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : null

const dataHora = (v?: string | null) =>
  v ? new Date(v).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

const dataCurta = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

function Secao({ n, titulo, apoio, children, erro }: {
  n: number | string; titulo: string; apoio?: string; children: any; erro?: boolean
}) {
  return (
    <section className={`rounded-lg border bg-card ${erro ? 'border-rose-400 dark:border-rose-800' : ''}`}>
      <header className="flex items-baseline gap-3 px-5 pt-5 pb-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full
                         bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
          {n}
        </span>
        <div>
          <h2 className="text-[15px] font-semibold leading-tight">{titulo}</h2>
          {apoio && <p className="mt-1 text-[13px] text-muted-foreground">{apoio}</p>}
        </div>
      </header>
      <div className="px-5 pb-5 pl-14 space-y-3">{children}</div>
    </section>
  )
}

const campo = 'w-full rounded-md border bg-background px-3 py-2 text-sm ' +
              'focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60'

const Rotulo = ({ children, opcional }: { children: any; opcional?: boolean }) => (
  <label className="block text-[13px] font-medium mb-1.5">
    {children}
    {opcional && <span className="ml-1.5 font-normal text-muted-foreground">opcional</span>}
  </label>
)

export default function RdcFormPage() {
  const nav = useNavigate()
  const { id } = useParams()
  const [qs] = useSearchParams()
  const novo = !id

  const [rdc, setRdc] = useState<any>(null)
  const [opps, setOpps] = useState<Opp[]>([])
  const [carregando, setCarregando] = useState(!novo)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [tentou, setTentou] = useState(false)
  const topo = useRef<HTMLDivElement>(null)

  const [form, setForm] = useState({
    opportunity_id: qs.get('oportunidade') || '',
    tipo_comercial: 'C',
    descricao_cliente: '',
    interpretacao_comercial: '',
    fase_relacionamento: '',
    urgencia_cliente: '',
    data_mencionada_cliente: '',
    gravacao_autorizada: true,
  })
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }))

  // No modo novo os anexos ficam em memoria ate o RDC existir; depois disso a lista
  // e sempre a do servidor.
  const [anexosLocais, setAnexosLocais] = useState<{ categoria: string; titulo: string; url: string }[]>([])
  const [novoAnexo, setNovoAnexo] = useState({ categoria: 'reuniao_cliente', titulo: '', url: '' })
  const [erroAnexo, setErroAnexo] = useState<string | null>(null)
  const [confirmacaoEm, setConfirmacaoEm] = useState('')
  const [agenda, setAgenda] = useState({ scheduled_at: '', meet_url: '' })

  const carregar = useCallback(async () => {
    if (!id) return
    const { data } = await api.get(`/v1/rdc/${id}`)
    setRdc(data)
    setForm({
      opportunity_id: data.opportunity_id,
      tipo_comercial: data.tipo_comercial,
      descricao_cliente: data.descricao_cliente || '',
      interpretacao_comercial: data.interpretacao_comercial || '',
      fase_relacionamento: data.fase_relacionamento || '',
      urgencia_cliente: data.urgencia_cliente || '',
      data_mencionada_cliente: data.data_mencionada_cliente || '',
      gravacao_autorizada: !!data.gravacao_autorizada,
    })
    setCarregando(false)
  }, [id])

  useEffect(() => { carregar() }, [carregar])

  useEffect(() => {
    api.get('/v1/opportunities/', { params: { per_page: 500 } })
      .then(({ data }) => setOpps(data.items || []))
      .catch(() => setOpps([]))
  }, [])

  const opp = useMemo(() => opps.find((o) => o.id === form.opportunity_id),
                      [opps, form.opportunity_id])
  const tipo = TIPOS.find((t) => t.v === form.tipo_comercial)!

  const emColeta = novo || rdc?.status === 'coleta'
  const editavel = emColeta
  const anexos = novo ? anexosLocais : (rdc?.anexos || [])

  const faltaOpp = !form.opportunity_id
  const faltaDesc = form.descricao_cliente.trim().length < 10
  const temProva = anexos.some((a: any) => CATEGORIAS.find((c) => c.v === a.categoria)?.prova)
  const temConfirmacao = form.gravacao_autorizada
    || !!confirmacaoEm || !!rdc?.confirmacao_escrita_em
  const podeEncerrar = !faltaOpp && !faltaDesc && temProva && temConfirmacao

  async function comErro(fn: () => Promise<any>, depois?: () => void) {
    setErro(null); setSalvando(true)
    try { await fn(); if (!novo) await carregar(); depois?.() }
    catch (e: any) {
      const d = e?.response?.data?.detail
      setErro(typeof d === 'string' ? d : 'Não foi possível concluir a ação.')
      topo.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } finally { setSalvando(false) }
  }

  const corpo = () => {
    const p: any = { ...form }
    Object.keys(p).forEach((k) => { if (p[k] === '') delete p[k] })
    return p
  }

  /** Cria o RDC e permanece nesta tela, agora com id na rota. */
  async function criar(encerrar: boolean) {
    setTentou(true)
    if (faltaOpp || faltaDesc) {
      topo.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    setErro(null); setSalvando(true)
    let novoId = ''
    try {
      const { data } = await api.post('/v1/rdc/', corpo())
      novoId = data.id
      for (const a of anexosLocais) {
        await api.post(`/v1/rdc/${novoId}/anexos`, { ...a, tipo: 'link' })
      }
      if (confirmacaoEm) {
        await api.put(`/v1/rdc/${novoId}`, { confirmacao_escrita_em: confirmacaoEm })
      }
      if (encerrar) await api.post(`/v1/rdc/${novoId}/registrar`)
      setAnexosLocais([])
      setAviso(encerrar
        ? 'Coleta encerrada. O prazo da agenda de passagem começou a correr e os diretores foram avisados.'
        : 'Demanda registrada em coleta. Ela só entra no prazo da Tecnologia quando a coleta for encerrada.')
      nav(`/rdc/${novoId}`, { replace: true })
    } catch (e: any) {
      const d = e?.response?.data?.detail
      const msg = typeof d === 'string' ? d : 'Não foi possível concluir. Revise os campos.'
      if (novoId) {
        setErro(`${msg} O RDC foi criado e continua em coleta nesta tela.`)
        nav(`/rdc/${novoId}`, { replace: true })
      } else {
        setErro(msg)
        topo.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } finally { setSalvando(false) }
  }

  const salvarEdicao = () => comErro(
    () => api.put(`/v1/rdc/${id}`, corpo()),
    () => setAviso('Alterações salvas.'))

  const encerrarColeta = () => comErro(
    () => api.post(`/v1/rdc/${id}/registrar`),
    () => setAviso('Coleta encerrada. O prazo da agenda de passagem começou a correr.'))

  const addAnexo = () => {
    setErroAnexo(null)
    if (!novoAnexo.titulo.trim() || !novoAnexo.url.trim()) {
      setErroAnexo('Informe o título e o link do anexo.')
      return
    }
    if (novo) {
      setAnexosLocais((a) => [...a, { ...novoAnexo }])
      setNovoAnexo({ categoria: 'reuniao_cliente', titulo: '', url: '' })
      return
    }
    comErro(
      () => api.post(`/v1/rdc/${id}/anexos`, { ...novoAnexo, tipo: 'link' }),
      () => setNovoAnexo({ categoria: 'reuniao_cliente', titulo: '', url: '' }))
  }

  const removerAnexo = (a: any, i: number) => {
    if (novo) { setAnexosLocais((l) => l.filter((_, j) => j !== i)); return }
    comErro(() => api.delete(`/v1/rdc/${id}/anexos/${a.id}`))
  }

  const gravarConfirmacao = () => {
    if (novo) return
    comErro(() => api.put(`/v1/rdc/${id}`, { confirmacao_escrita_em: confirmacaoEm }),
            () => setConfirmacaoEm(''))
  }

  const marcarAgenda = () => comErro(async () => {
    if (!agenda.scheduled_at) throw { response: { data: { detail: 'Informe data e hora da agenda.' } } }
    await api.post(`/v1/rdc/${id}/agendas`, {
      scheduled_at: new Date(agenda.scheduled_at).toISOString(),
      meet_url: agenda.meet_url || undefined, duracao_min: 30,
    })
    setAgenda({ scheduled_at: '', meet_url: '' })
  })

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
      </div>
    )
  }

  const estado = rdc ? (ESTADOS[rdc.status] || { t: rdc.status, cls: 'bg-slate-100 text-slate-700' }) : null

  return (
    <div>
      {/* ------------------------------------------------ cabeçalho */}
      <div ref={topo} className="px-6 pt-6">
        <Link to="/rdc" className="inline-flex items-center gap-1 text-sm text-muted-foreground
                                   hover:text-foreground transition">
          <ChevronLeft className="h-4 w-4" /> Registros de Demanda Comercial
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <ClipboardList className="h-6 w-6" />
            {rdc ? rdc.code : 'Nova demanda'}
          </h1>
          {estado && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${estado.cls}`}>
              {estado.t}
            </span>
          )}
          {rdc?.suspenso && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
              <PauseCircle className="h-3.5 w-3.5" /> prazo suspenso
            </span>
          )}
        </div>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {emColeta
            ? 'Registre em até 1 dia útil da reunião. O relógio da Tecnologia só começa a correr quando a coleta for encerrada — antes disso a demanda não existe para efeito do procedimento.'
            : 'A coleta está encerrada. Daqui em diante o registro segue pela passagem, pelo de acordo e pelo parecer.'}
        </p>
      </div>

      {erro && (
        <div className="mx-6 mt-4 flex items-start gap-2 rounded-md border border-rose-300
                        bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900
                        dark:bg-rose-950/30 dark:text-rose-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {erro}
        </div>
      )}
      {aviso && !erro && (
        <div className="mx-6 mt-4 flex items-start gap-2 rounded-md border border-emerald-300
                        bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900
                        dark:bg-emerald-950/30 dark:text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {aviso}
        </div>
      )}

      <div className="mt-5 grid gap-5 px-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {/* ---------------------------------------- 1. oportunidade */}
          <Secao n={1} titulo="A que oportunidade esta demanda pertence"
                 apoio="Todo RDC nasce amarrado a uma oportunidade — é ela que dá o cliente e o contexto."
                 erro={tentou && faltaOpp}>
            <select value={form.opportunity_id} className={campo} disabled={!novo}
                    onChange={(e) => set('opportunity_id', e.target.value)}>
              <option value="">Selecione a oportunidade…</option>
              {opps.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}{o.lead_company_name ? ` — ${o.lead_company_name}` : ''}
                </option>
              ))}
            </select>
            {tentou && faltaOpp && (
              <p className="text-[13px] text-rose-600 dark:text-rose-400">
                Escolha a oportunidade a que esta demanda pertence.
              </p>
            )}
            {opp && (
              <div className="rounded-md border bg-muted/30 p-4">
                <div className="flex items-start gap-2">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium leading-tight">
                      {opp.lead_company_name || opp.lead_title || '—'}
                    </div>
                    <div className="mt-0.5 text-[13px] text-muted-foreground">{opp.name}</div>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] sm:grid-cols-4">
                  {([
                    ['Estágio', ESTAGIOS[opp.stage || ''] || opp.stage],
                    ['Valor', moeda(opp.value)],
                    ['Responsável', opp.owner_name],
                    ['Prospecção', opp.lead_sub_origin],
                  ] as const).map(([r, v]) => v ? (
                    <div key={r}>
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{r}</dt>
                      <dd className="mt-0.5">{v}</dd>
                    </div>
                  ) : null)}
                </dl>
                <Link to={`/opportunities/${form.opportunity_id}`}
                      className="mt-3 inline-block text-[13px] text-primary hover:underline">
                  Abrir a oportunidade
                </Link>
              </div>
            )}
          </Secao>

          {/* ---------------------------------------- 2. demanda */}
          <Secao n={2} titulo="O que o cliente pediu"
                 apoio="Primeiro as palavras dele; sua leitura vem depois, em campo separado."
                 erro={tentou && faltaDesc}>
            <div>
              <Rotulo>Demanda, nas palavras do cliente</Rotulo>
              <textarea rows={5} value={form.descricao_cliente} className={campo} disabled={!editavel}
                        onChange={(e) => set('descricao_cliente', e.target.value)}
                        placeholder="Transcreva o pedido como ele foi feito, sem traduzir para linguagem de produto." />
              <div className="mt-1 flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  É este texto que a Tecnologia lê na passagem.
                </p>
                {editavel && faltaDesc && (
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    mínimo de 10 caracteres
                  </span>
                )}
              </div>
              {tentou && faltaDesc && (
                <p className="mt-1 text-[13px] text-rose-600 dark:text-rose-400">
                  Descreva a demanda nas palavras do cliente (ao menos 10 caracteres).
                </p>
              )}
            </div>
            <div>
              <Rotulo opcional>Sua interpretação comercial</Rotulo>
              <textarea rows={3} value={form.interpretacao_comercial} className={campo}
                        disabled={!editavel}
                        onChange={(e) => set('interpretacao_comercial', e.target.value)}
                        placeholder="O que você entende que isso significa em termos de produto." />
            </div>
            {rdc?.entendimento_acordado && (
              <div className="rounded-md border-l-2 border-sky-500 bg-sky-50 p-3 text-[13px]
                              dark:bg-sky-950/20">
                <div className="font-medium">Entendimento acordado na passagem</div>
                <p className="mt-1">{rdc.entendimento_acordado}</p>
                {rdc.plano_acoes && (
                  <p className="mt-2 text-muted-foreground">Plano de ações: {rdc.plano_acoes}</p>
                )}
              </div>
            )}
          </Secao>

          {/* ---------------------------------------- 3. classificação */}
          <Secao n={3} titulo="Classificação provisória"
                 apoio="A Tecnologia confirma ou reclassifica na passagem, e a reclassificação prevalece.">
            <div className="space-y-2">
              {TIPOS.map((t) => {
                const sel = form.tipo_comercial === t.v
                const tec = rdc?.tipo_tecnico === t.v && rdc?.tipo_tecnico !== rdc?.tipo_comercial
                return (
                  <button key={t.v} type="button" disabled={!editavel} aria-pressed={sel}
                          onClick={() => set('tipo_comercial', t.v)}
                          className={`flex w-full items-start gap-3 rounded-md border p-3 text-left
                                      transition disabled:cursor-default ${
                                        sel ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                            : tec ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                                                  : editavel ? 'hover:bg-muted/50' : 'opacity-60'}`}>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md
                                      text-sm font-semibold ${sel
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-muted-foreground'}`}>
                      {t.v}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">
                        {t.t}
                        {tec && <span className="ml-2 text-xs font-normal text-amber-700
                                                 dark:text-amber-400">reclassificado pela Tecnologia</span>}
                      </span>
                      <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">
                        {t.d}
                      </span>
                    </span>
                    {sel && <Check className="ml-auto mt-1 h-4 w-4 shrink-0 text-primary" />}
                  </button>
                )
              })}
            </div>
            {form.tipo_comercial === 'E' && emColeta && (
              <div className="flex items-start gap-2 rounded-md border-l-2 border-amber-500
                              bg-amber-50 p-3 text-[13px] dark:bg-amber-950/20">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  Tipo E não se oferta nem se dimensiona. Ao encerrar a coleta, a deliberação
                  do Diretor Presidente é aberta automaticamente.
                </span>
              </div>
            )}
          </Secao>

          {/* ---------------------------------------- 4. contexto */}
          <Secao n={4} titulo="Contexto declarado pelo cliente"
                 apoio="Urgência e data são informação do cliente, nunca compromisso da Nai.">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Rotulo opcional>Fase do relacionamento</Rotulo>
                <select value={form.fase_relacionamento} className={campo} disabled={!editavel}
                        onChange={(e) => set('fase_relacionamento', e.target.value)}>
                  <option value="">—</option>
                  {FASES.map((f) => <option key={f.v} value={f.v}>{f.t}</option>)}
                </select>
              </div>
              <div>
                <Rotulo opcional>Urgência declarada</Rotulo>
                <select value={form.urgencia_cliente} className={campo} disabled={!editavel}
                        onChange={(e) => set('urgencia_cliente', e.target.value)}>
                  <option value="">—</option>
                  {URGENCIAS.map((u) => <option key={u.v} value={u.v}>{u.t}</option>)}
                </select>
              </div>
              <div>
                <Rotulo opcional>Data citada pelo cliente</Rotulo>
                <input type="date" value={form.data_mencionada_cliente} className={campo}
                       disabled={!editavel}
                       onChange={(e) => set('data_mencionada_cliente', e.target.value)} />
              </div>
            </div>
          </Secao>

          {/* ---------------------------------------- 5. prova e anexos */}
          <Secao n={5} titulo="Prova da conversa"
                 apoio="A coleta só se encerra com a gravação/transcrição ou a confirmação escrita anexada.">
            <div className="grid gap-2 sm:grid-cols-2">
              {([
                [true, Mic, 'Gravação autorizada', 'O cliente autorizou gravar a reunião.'],
                [false, FileSignature, 'Sem autorização de gravação', 'A conversa não foi gravada.'],
              ] as const).map(([val, Icone, t, d]) => {
                const sel = form.gravacao_autorizada === val
                return (
                  <button key={String(val)} type="button" disabled={!editavel} aria-pressed={sel}
                          onClick={() => set('gravacao_autorizada', val)}
                          className={`flex items-start gap-2.5 rounded-md border p-3 text-left
                                      transition disabled:cursor-default ${sel
                                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                        : editavel ? 'hover:bg-muted/50' : 'opacity-60'}`}>
                    <Icone className={`mt-0.5 h-4 w-4 shrink-0 ${
                      sel ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span>
                      <span className="block text-sm font-medium">{t}</span>
                      <span className="mt-0.5 block text-[13px] text-muted-foreground">{d}</span>
                    </span>
                  </button>
                )
              })}
            </div>

            {emColeta && (
              <div className="flex items-start gap-2 rounded-md border-l-2 border-amber-500
                              bg-amber-50 p-3 text-[13px] dark:bg-amber-950/20">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  {form.gravacao_autorizada
                    ? 'Anexe abaixo o link da gravação ou da transcrição. Sem ele a coleta não se encerra.'
                    : 'Sem gravação, a única prova é a confirmação escrita enviada ao cliente no mesmo dia: informe quando foi enviada e anexe o link.'}
                </span>
              </div>
            )}

            {!form.gravacao_autorizada && (
              <div className="max-w-sm">
                <Rotulo>Confirmação escrita enviada ao cliente em</Rotulo>
                {rdc?.confirmacao_escrita_em ? (
                  <p className="text-sm text-muted-foreground">
                    Registrada em {dataHora(rdc.confirmacao_escrita_em)}.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                    <input type="datetime-local" value={confirmacaoEm} className={campo}
                           disabled={!editavel}
                           onChange={(e) => setConfirmacaoEm(e.target.value)} />
                    {!novo && (
                      <button onClick={gravarConfirmacao} disabled={salvando || !confirmacaoEm}
                              className="rounded-md border px-3 py-2 text-sm hover:bg-muted
                                         disabled:opacity-60">
                        Registrar envio
                      </button>
                    )}
                  </div>
                )}
                {!rdc?.confirmacao_escrita_em && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    O procedimento pede que seja no mesmo dia da conversa.
                  </p>
                )}
              </div>
            )}

            <div className="border-t pt-4">
              <div className="mb-2 flex items-center gap-1.5 text-[13px] font-medium">
                <Paperclip className="h-4 w-4" /> Anexos e links
              </div>
              {anexos.length === 0 ? (
                <p className="mb-3 text-[13px] text-muted-foreground">
                  Nenhum anexo ainda. Pelo menos um deve ser a gravação/transcrição ou a
                  confirmação escrita — é o que o procedimento aceita como prova da conversa.
                </p>
              ) : (
                <ul className="mb-3 space-y-1.5">
                  {anexos.map((a: any, i: number) => {
                    const cat = CATEGORIAS.find((c) => c.v === a.categoria)
                    return (
                      <li key={a.id || `${a.url}-${i}`}
                          className="flex items-start justify-between gap-3 rounded-md border px-3 py-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-sm">
                            <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            {a.url
                              ? <a href={a.url} target="_blank" rel="noreferrer"
                                   className="truncate font-medium text-primary hover:underline">{a.titulo}</a>
                              : <span className="truncate font-medium">{a.titulo}</span>}
                            {cat?.prova && (
                              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5
                                               text-[11px] font-medium text-emerald-800
                                               dark:bg-emerald-900/40 dark:text-emerald-300">
                                prova
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                            {cat?.t || a.categoria}
                          </div>
                        </div>
                        <button type="button" aria-label="Remover anexo" disabled={salvando}
                                onClick={() => removerAnexo(a, i)}
                                className="shrink-0 rounded p-1 hover:bg-muted">
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}

              <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                <select value={novoAnexo.categoria} className={campo}
                        onChange={(e) => setNovoAnexo({ ...novoAnexo, categoria: e.target.value })}>
                  {CATEGORIAS.filter((c) => emColeta ? c.prova || c.v === 'outro' : true)
                    .map((c) => <option key={c.v} value={c.v}>{c.t}</option>)}
                </select>
                <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
                  <input placeholder="Título" value={novoAnexo.titulo} className={campo}
                         onChange={(e) => setNovoAnexo({ ...novoAnexo, titulo: e.target.value })} />
                  <input placeholder="https://…" value={novoAnexo.url} className={campo}
                         onChange={(e) => setNovoAnexo({ ...novoAnexo, url: e.target.value })} />
                  <button type="button" onClick={addAnexo} disabled={salvando}
                          className="inline-flex items-center justify-center gap-1.5 rounded-md
                                     border bg-background px-4 py-2 text-sm hover:bg-muted">
                    <Plus className="h-4 w-4" /> Adicionar
                  </button>
                </div>
              </div>
              {erroAnexo && (
                <p className="mt-1.5 text-[13px] text-rose-600 dark:text-rose-400">{erroAnexo}</p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Os anexos são links — Drive, Meet, Gmail. O upload de arquivo para dentro
                do CRM ainda não existe.
              </p>
            </div>
          </Secao>

          {/* ---------------------------------------- 6. agenda */}
          {rdc && !emColeta && (
            <Secao n={6} titulo="Agenda de passagem"
                   apoio="Obrigatoriamente gravada e transcrita — sem as duas, a passagem não produz efeito e precisa ser refeita.">
              {(rdc.agendas || []).length === 0 && (
                <p className="text-[13px] text-muted-foreground">
                  Nenhuma agenda marcada. A Tecnologia tem até {dataCurta(rdc.prazo_agenda_em)} para marcar.
                </p>
              )}
              {(rdc.agendas || []).map((a: any) => (
                <div key={a.id} className="rounded-md border px-3 py-2 text-sm">
                  {dataHora(a.scheduled_at)}
                  {a.realizada
                    ? <span className="ml-2 text-xs text-emerald-600">realizada</span>
                    : <span className="ml-2 text-xs text-amber-600">agendada</span>}
                  {a.meet_url && (
                    <a href={a.meet_url} target="_blank" rel="noreferrer"
                       className="ml-2 text-xs text-primary hover:underline">abrir Meet</a>
                  )}
                  {a.de_acordo_comercial_em && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      de acordo Comercial em {dataCurta(a.de_acordo_comercial_em)}
                    </span>
                  )}
                  {a.de_acordo_tecnico_em && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      · Tecnologia em {dataCurta(a.de_acordo_tecnico_em)}
                    </span>
                  )}
                </div>
              ))}
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <input type="datetime-local" value={agenda.scheduled_at} className={campo}
                       onChange={(e) => setAgenda({ ...agenda, scheduled_at: e.target.value })} />
                <input placeholder="Link do Meet" value={agenda.meet_url} className={campo}
                       onChange={(e) => setAgenda({ ...agenda, meet_url: e.target.value })} />
                <button onClick={marcarAgenda} disabled={salvando}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md
                                   border px-4 py-2 text-sm hover:bg-muted">
                  <CalendarPlus className="h-4 w-4" /> Marcar
                </button>
              </div>
            </Secao>
          )}

          {/* ---------------------------------------- 7. etapa técnica */}
          {rdc && !emColeta && rdc.pareceres !== null && rdc.pareceres !== undefined && (
            <Secao n={7} titulo="Etapa técnica"
                   apoio="Visível a Comercial, Tecnologia, Financeiro e Presidência.">
              <RdcDiretoria r={rdc} onErro={setErro} onMudou={carregar} />
            </Secao>
          )}
        </div>

        {/* ------------------------------------------------ coluna lateral */}
        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          {rdc && !emColeta && (
            <div className="rounded-lg border bg-card p-5">
              <h3 className="text-sm font-semibold">Situação</h3>
              <dl className="mt-3 space-y-2 text-[13px]">
                {([
                  ['Registrado em', dataCurta(rdc.registrado_em)],
                  ['Prazo da agenda', dataCurta(rdc.prazo_agenda_em)],
                  ['Parecer preliminar', dataCurta(rdc.prazo_preliminar_em)],
                  ['Parecer conclusivo', dataCurta(rdc.prazo_conclusivo_em)],
                ] as const).map(([r, v]) => (
                  <div key={r} className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">{r}</dt>
                    <dd className="tabular-nums">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className={`mt-4 rounded-md border-l-2 p-3 text-[13px] ${rdc.pode_ofertar
                ? 'border-emerald-500 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'
                : 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/20 dark:text-amber-200'}`}>
                {rdc.pode_ofertar
                  ? 'Oferta liberada nos limites do que o parecer validou.'
                  : rdc.bloqueio_oferta}
              </div>
              {rdc.frase_para_cliente && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide
                                  text-muted-foreground">
                    <MessageSquareQuote className="h-3.5 w-3.5" /> O que dizer ao cliente hoje
                  </div>
                  <p className="mt-1 text-[13px] italic">{rdc.frase_para_cliente}</p>
                </div>
              )}
            </div>
          )}

          {emColeta && (
            <>
              <div className="rounded-lg border bg-card p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <CalendarClock className="h-4 w-4" /> O que este registro dispara
                </h3>
                <ol className="mt-4 space-y-4">
                  {[
                    ['Registro da coleta', 'Ao encerrar a coleta com a prova anexada.'],
                    ['Agenda de passagem', 'A Tecnologia agenda em até 2 dias úteis.'],
                    ['Parecer preliminar', '2 dias úteis depois da passagem.'],
                    ['Parecer conclusivo', tipo.prazo
                      ? `${tipo.prazo} dias úteis para o tipo ${tipo.v}.`
                      : 'Tipo E vai direto à deliberação da Presidência.'],
                  ].map(([t, d], i, a) => (
                    <li key={t} className="relative flex gap-3">
                      <span className="mt-1 flex h-2 w-2 shrink-0 rounded-full bg-primary" />
                      {i < a.length - 1 && (
                        <span className="absolute left-[3px] top-4 h-[calc(100%+0.5rem)] w-px bg-border" />
                      )}
                      <span className="min-w-0">
                        <span className="block text-[13px] font-medium leading-tight">{t}</span>
                        <span className="mt-0.5 block text-[13px] text-muted-foreground">{d}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
                  Prazos suspendem quando falta insumo do cliente ou do Comercial, e voltam
                  a correr na retomada.
                </p>
              </div>

              <div className="rounded-lg border bg-card p-5">
                <h3 className="text-sm font-semibold">Para encerrar a coleta</h3>
                <ul className="mt-3 space-y-2">
                  {([
                    [!faltaOpp, 'Oportunidade escolhida'],
                    [!faltaDesc, 'Demanda nas palavras do cliente'],
                    [temProva, 'Prova anexada (gravação ou confirmação escrita)'],
                    [temConfirmacao, form.gravacao_autorizada
                      ? 'Gravação autorizada pelo cliente'
                      : 'Data da confirmação escrita'],
                  ] as const).map(([ok, t]) => (
                    <li key={t} className="flex items-start gap-2 text-[13px]">
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center
                                        rounded-full border ${ok
                                          ? 'border-emerald-500 bg-emerald-500 text-white'
                                          : 'border-muted-foreground/40'}`}>
                        {ok && <Check className="h-3 w-3" strokeWidth={3} />}
                      </span>
                      <span className={ok ? '' : 'text-muted-foreground'}>{t}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
                  Sem tudo isso a demanda pode ficar salva, mas o prazo da Tecnologia não
                  começa a correr.
                </p>
              </div>
            </>
          )}

          <div className="rounded-lg border-l-2 border-amber-500 bg-amber-50 p-4
                          text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
            <h3 className="text-sm font-semibold">Enquanto não houver parecer</h3>
            <p className="mt-1.5 text-[13px]">
              Não formule oferta, prazo ou estimativa ao cliente sobre esta demanda.
            </p>
          </div>
        </aside>
      </div>

      {/* ------------------------------------------------ ações */}
      <div className="sticky bottom-0 z-30 mt-5 border-t bg-card/95 backdrop-blur
                      supports-[backdrop-filter]:bg-card/80">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
          <p className="hidden max-w-md text-[13px] text-muted-foreground sm:block">
            {!emColeta
              ? 'Coleta encerrada. As próximas etapas acontecem nesta mesma tela.'
              : podeEncerrar
                ? 'Encerrar a coleta parte o prazo de 2 dias úteis da agenda de passagem.'
                : 'Sem a prova da conversa a coleta fica aberta: dá para salvar agora e concluir depois.'}
          </p>
          <div className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => nav('/rdc')} disabled={salvando}
                    className="rounded-md border px-4 py-2 text-sm hover:bg-muted">
              {emColeta ? 'Cancelar' : 'Voltar à lista'}
            </button>

            {novo && (
              <button onClick={() => criar(false)} disabled={salvando}
                      className="inline-flex items-center gap-2 rounded-md border px-4 py-2
                                 text-sm hover:bg-muted disabled:opacity-60">
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar e concluir depois
              </button>
            )}
            {!novo && emColeta && (
              <button onClick={salvarEdicao} disabled={salvando}
                      className="inline-flex items-center gap-2 rounded-md border px-4 py-2
                                 text-sm hover:bg-muted disabled:opacity-60">
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar alterações
              </button>
            )}
            {emColeta && (
              <button onClick={() => (novo ? criar(true) : encerrarColeta())}
                      disabled={salvando || !podeEncerrar}
                      title={podeEncerrar ? undefined
                                          : 'Anexe a prova da conversa para encerrar a coleta'}
                      className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2
                                 text-sm font-medium text-primary-foreground hover:opacity-90
                                 disabled:opacity-50">
                {salvando ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <CheckCircle2 className="h-4 w-4" />}
                Encerrar coleta e registrar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
